<template>
  <!-- With tooltip wrapper -->
  <el-tooltip v-if="showNameOnHover" :show-arrow="true" placement="top">
    <template #content>
      <template v-if="namePrefix">
        <strong>{{namePrefix}}:</strong><br >
      </template>
      {{personName}}
    </template>
    <div
      v-bind="$attrs"
      :style="customStyle"
      :class="backgroundColorClass"
      :title="personName"
      class="avatar"
      @focus="$emit('focus')"
      @blur="$emit('blur')"
    >
      <div v-if="hasImage" :style="photoStyle" class="avatar__image" />
      <span v-else :style="fontStyle">{{ firstLetter }}</span>
    </div>
  </el-tooltip>
  <!-- Without tooltip wrapper -->
  <div
    v-else
    v-bind="$attrs"
    :style="customStyle"
    :class="backgroundColorClass"
    :title="personName"
    class="avatar"
    @focus="$emit('focus')"
    @blur="$emit('blur')"
  >
    <div v-if="hasImage" :style="photoStyle" class="avatar__image" />
    <span v-else :style="fontStyle">{{ firstLetter }}</span>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { IPersonBase } from '@/api/types/response.types'

export default defineComponent({
  name: 'Avatar',

  // el-tooltip drops attributes it doesn't use, so the parent's class and style go on the avatar itself in both
  // branches, as they did when Element UI's tooltip rendered it as the root
  inheritAttrs: false,

  props: {
    person: {
      type: Object as () => IPersonBase,
      default: null,
    },
    size: {
      type: Number,
      default: 32,
    },
    showNameOnHover: {
      type: Boolean,
      default: false,
    },
    /* Prepends to the name for the hover and title states */
    namePrefix: {
      type: String,
      default: null,
    },
  },

  emits: ['blur', 'focus'],

  data() {
    return {
      backgroundColorCircle: [
        'bluey-purple',
        'darkish-pink',
        'reddish-orange',
        'light-orange',
        'dodger-blue',
        'algae-green',
        'bluish-grey',
      ],
    }
  },

  computed: {
    isAnonymous(): boolean {
      if (this.person && this.person.id !== 0) {
        return false
      }
      return true
    },
    personName(): string {
      return this.isAnonymous ? 'Unassigned' : this.person.name
    },
    firstLetter(): string {
      if (this.isAnonymous || this.person.photoUrl) {
        return ''
      }

      if (this.person.name) {
        return this.person.name[0].toUpperCase()
      }

      return ''
    },

    customStyle(): Record<string, any> {
      return {
        width: `${this.size}px`,
        height: `${this.size}px`,
        borderRadius: '50%',
      }
    },

    photoStyle(): Record<string, any> {
      return {
        backgroundImage: 'url(' + this.person.photoUrl + ')',
        backgroundPosition: 'center',
        backgroundSize: 'cover',
      }
    },

    fontStyle(): Record<string, any> {
      return {
        fontSize: this.size / 1.5 + 'px',
        fontWeight: 500,
        color: '#fff',
      }
    },

    hasImage(): boolean {
      return this.isAnonymous ? false : Boolean(this.person.photoUrl)
    },

    backgroundColorClass(): string {
      if (this.isAnonymous) {
        return 'avatar--is-anonymous'
      }
      if (this.hasImage) {
        return 'avatar--white'
      }
      const colors = this.backgroundColorCircle
      return 'avatar--' + this.randomBackgroundColor(colors)
    },
  },

  methods: {
    randomBackgroundColor(colors: string[]) {
      const letters = this.person.name.split('') // get string as an array of chars
      // Sum all ASCII letter codes
      const seed = letters.reduce((acc, value) => {
        return acc + value.charCodeAt(0)
      }, 0)

      return colors[seed % colors.length]
    },
  },
})
</script>

<style lang="scss">
.avatar {
  outline: none;
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  vertical-align: middle;
  flex-shrink: 0;

  .avatar__image {
    position: absolute;
    width: 104%;
    height: 104%;
  }

  &--is-anonymous {
    background-color: var(--color-background-secondary) !important;
    border: 1.5px dashed $cool-grey;
  }

  &--white {
    background-color: var(--color-general-white);
  }

  &--bluey-purple {
    background-color: $bluey-purple;
  }

  &--darkish-pink {
    background-color: $darkish-pink;
  }

  &--reddish-orange {
    background-color: $reddish-orange;
  }

  &--light-orange {
    background-color: $light-orange;
  }

  &--dodger-blue {
    background-color: $color--blue;
  }

  &--algae-green {
    background-color: $algae-green;
  }

  &--bluish-grey {
    background-color: var(--color-text-third);
  }

  &--duck-egg-blue {
    background-color: $duck-egg-blue;
  }

  &--light-peach {
    background-color: $light-peach;
  }

  &--very-light-purple {
    background-color: $very-light-purple;
  }
}
</style>
